import './style.scss';
import { ApplySymbolInput } from '../hooks';
import { LeaseFundFullInfo } from 'views/pages/symbol/module/fullInfo';
import { LeaseFundThumbnail } from 'views/pages/symbol/module/thumbnail';
import { LeaseFundBasicInfo } from 'views/pages/symbol/module/basicInfo';
import { Description, DescriptionAndLink, labelColor, labelSize } from 'views/mutual/description';
import { LotInput_LeaseFund } from 'views/pages/symbol/module/lotInput';
import { Caption } from 'views/mutual/caption';
import { CheckLabel, CheckLabelTextSize } from 'views/mutual/checkLabel';
import { Button, ButtonFill, ButtonSize } from 'views/mutual/button';
import { SymbolDetailInfo, useSendPreContractNotifyMutation } from 'views/utils/graphql/graphql';
import { FormikProps } from 'formik';
import { useCustomLocationName } from 'views/mutual/location/hooks';
import { icons } from 'constants/icons';
import { IconColor, IconSize, SvgIcon } from 'views/mutual/svgIcon';
import { useMessageModal } from 'views/mutual/modal/messageModal/hooks';
import { MessageModal } from 'views/mutual/modal/messageModal';
import { formatPrice } from 'views/utils/converter';
import { UnitSelectorPosition } from 'views/templates/module/unitSelector';
// import { SelectorProps } from 'views/templates/module/selector'
import { UnitInputPosition } from 'views/templates/module/unitInput';
import { AlignmentType } from 'views/templates/module/inputTemplate';
import { date } from 'yup';
import { PagePath, PagePathFactory } from 'constants/pagePath';
import { useNavigate } from 'react-router-dom';
import { ProductType } from 'constants/productType';

type SymbolDetailProps = {
  symbolDetail: SymbolDetailInfo;
  formik: FormikProps<ApplySymbolInput>;
  interest: number | null;
  totalAmountRaw: number | null;
  canApplySymbol: boolean;
};

export const LeaseFundSymbolDetail = ({
  symbolDetail,
  formik,
  interest,
  totalAmountRaw,
  canApplySymbol,
}: SymbolDetailProps) => {
  useCustomLocationName(symbolDetail.SymbolName);
  const { modalProps, showMessageModal } = useMessageModal();
  const navigate = useNavigate();
  const isLotInputDisabled =
    !symbolDetail.Tradable ||
    !symbolDetail.AmountOptions ||
    symbolDetail.SalesUnitCtrl == null ||
    symbolDetail.MaxInvestmentAmount == null;
  return (
    <div className="leaseFund">
      {!symbolDetail.Tradable && (
        <div className="disableApplyDescription">
          <Description
            label="こちらの案件は現在お申し込みいただけません"
            labelColor={labelColor.red}
            labelSize={labelSize.large}
          />
        </div>
      )}
      <div className="programDetail">
        <div className="programDetail_card">
          <LeaseFundThumbnail {...symbolDetail} />
          <LeaseFundBasicInfo isFull={true} {...symbolDetail} />
          <LeaseFundFullInfo {...symbolDetail!} />
          <div className="programDetail_card_wrapper">
            <a
              className="documentLink"
              href={symbolDetail.PreContractDocumentURL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                formik.setFieldValue('isContractDocumentOpened', true);
              }}
            >
              <Button buttonFill={ButtonFill.white} buttonSize={ButtonSize.small}>
                <div className="documentLink_content">
                  <SvgIcon icon={icons.file} color={IconColor.blue} size={IconSize.icon_xs} />
                  <span className="documentLink_content_label">契約締結前交付書面</span>
                </div>
              </Button>
            </a>
            <a
              className="documentLink"
              href={symbolDetail.SimplePreContractDocumentURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button buttonFill={ButtonFill.white} buttonSize={ButtonSize.small}>
                <div className="documentLink_content">
                  <SvgIcon icon={icons.file} color={IconColor.blue} size={IconSize.icon_xs} />
                  <span className="documentLink_content_label">簡易版</span>
                </div>
              </Button>
            </a>
          </div>
          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※1　賃借人のほかに転借人、保証人等の実質的な与信先が存在する場合は、当該与信先の名称を記載しています。また、賃借人等の名称は正式名称ではなく略称を記載しています。事業の仕組みの詳細および賃借人等の正式名称については、案件資料をご確認ください。"
          />
          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※2　購入選択権設定日の経過期間は、本日～購入選択権設定日までの期間を表し、販売資料に表示する経過時期とは異なります。"
          />
          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※3　出資をお申込みいただいた場合でも、本案件の残高によっては、お申込みを承れない場合があります。予めご了承ください。"
          />
          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※4　会社法上の法人（株式会社、合同会社等）および特例有限会社以外の法人のお客様は、念書、定款等の提出をお願いする場合がございます。"
          />
        </div>
        <div className="programDetail_request">
          <LotInput_LeaseFund
            interest={interest}
            accruedInterest={symbolDetail.AccruedInterest}
            totalAmountRaw={totalAmountRaw}
            disabled={isLotInputDisabled}
            error={formik.touched.amount ? formik.errors.amount : ''}
            onChange={(value: string) => formik.setFieldValue('amount', parseInt(value.replaceAll(',', '')))}
            value={formik.values.amount}
            amountOptionsRaw={symbolDetail.AmountOptions}
            step={symbolDetail.SalesUnitCtrl}
            maxQuantity={symbolDetail.MaxInvestmentAmount}
            dateInputProps={{
              date: formik.values.transferDate,
              name: 'transferDate',
              // 過去日かどうかを考慮してTransferStartDateが返ってくるので過去日かどうかをクライアントは考慮しない
              minDate: symbolDetail.TransferStartDate ? new Date(symbolDetail.TransferStartDate) : null,
              maxDate: symbolDetail.TransferEndDate ? new Date(symbolDetail.TransferEndDate) : null,
              onChange: (value) => formik.setFieldValue('transferDate', value),
              onBlur: formik.handleBlur,
              isWeekEndDisable: true,
              title: '振込予定日',
              error: formik.touched.transferDate ? formik.errors.transferDate : '',
            }}
          />
          <Caption label="確認事項" />
          <Description
            labelColor={labelColor.red}
            labelSize={labelSize.medium}
            label="すべての確認事項にチェックを入れると「申込内容の確認へ」がクリックできます。"
          />

          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※「オペレーティング・リース事業投資案件の契約締結前交付書面」のURLをクリックすることでチェックできるようになります。"
          />
          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※書面のダウンロードが必要な場合はブラウザに表示した後、ブラウザの機能で保存可能です。"
          />
          <CheckLabel
            checked={formik.values.isContractDocumentCheck}
            onChange={() => formik.setFieldValue('isContractDocumentCheck', !formik.values.isContractDocumentCheck)}
            link="「オペレーティング・リース事業投資案件の契約締結前交付書面」"
            href={symbolDetail.PreContractDocumentURL}
            after={
              <>
                をダウンロードし、その記載内容をよく読んで理解しました。
                <DescriptionAndLink
                  labelColor={labelColor.gray}
                  labelSize={labelSize.small}
                  before="※記載内容について、"
                  link={PagePathFactory.faqLinkSymbolProductType(ProductType.LeaseFund)}
                  linkText="よくあるご質問と回答（FAQ）"
                  after="をまとめておりますのでご参照ください。さらにご質問がある場合は、画面上部の「お問い合わせ」からご連絡いただければ担当者よりお電話などにてご説明いたします。"
                />
                <Description
                  labelColor={labelColor.gray}
                  labelSize={labelSize.small}
                  label="※記載内容を書面で交付することを希望される場合は「お問い合わせ」からお申し出ください。その場合、速やかにこの画面での契約締結を停止します。"
                />
              </>
            }
            linkClicked={formik.values.isContractDocumentOpened}
            onClick={() => {
              formik.setFieldValue('isContractDocumentOpened', true);
            }}
            linkClickedRequired={true}
            errorText="リンクをクリックして、「オペレーティング・リース事業投資案件の契約締結前交付書面」の内容を確認してください"
          />
          <CheckLabel
            checked={formik.values.isCheck2}
            onChange={() => formik.setFieldValue('isCheck2', !formik.values.isCheck2)}
            before="貴社所定の審査の結果によっては、本事業への出資ができないことがあることを確認しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck3}
            onChange={() => formik.setFieldValue('isCheck3', !formik.values.isCheck3)}
            before="本事業への出資が申込人の意向に沿った内容となっていることを確認しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck4}
            onChange={() => formik.setFieldValue('isCheck4', !formik.values.isCheck4)}
            before="賃借人等の債務不履行・倒産のリスク・残存価格リスクおよび為替リスクその他上記の契約締結前交付書面に記載の投資リスクについてすべて理解しました。これらの投資リスクにより、場合によっては出資金（当初出資金のほか追加出資金を含む）の全額または一部を回収できないことがあることを理解しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck5}
            onChange={() => formik.setFieldValue('isCheck5', !formik.values.isCheck5)}
            before="株式会社FPGより勧誘を受けるに際し、不確実な事実について、断定的判断の提供を受けたり、確実であると誤解させる恐れのあることを告げられたりした事実はないことを確認しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck6}
            onChange={() => formik.setFieldValue('isCheck6', !formik.values.isCheck6)}
            before="出資する金融商品が預金等ではなく、預金保険法第五十三条に規定する保険金の支払い（いわゆる「預金保険機構」による保険金の支払い）の対象とならないことを確認しました。"
          />
          <CheckLabel
            checked={formik.values.isCheck7}
            onChange={() => formik.setFieldValue('isCheck7', !formik.values.isCheck7)}
            before={`お客さまの氏名その他の申告事項に変更がないことを確認しました。
              （変更がある場合は、右上の「登録情報＞ご登録内容の照会/変更」より、変更をお願いいたします）`}
          />
          {/* <CheckLabel
            checked={formik.values.isCheck8}
            onChange={() => formik.setFieldValue('isCheck8', !formik.values.isCheck8)}
            before="ご紹介者さまとの紹介契約について、異なるお取り扱いとなる場合があることを了解しました。"
          /> */}
          <CheckLabel
            checked={formik.values.isCheck8}
            onChange={() => formik.setFieldValue('isCheck8', !formik.values.isCheck8)}
            before="本申込みの撤回ができないことを確認しました。"
          />
          {/* 紹介者情報を非表示 */}
          {/* <div className="programDetail_introductionConfirmation">
            <div>本お申込みにあたり、紹介者さまから、紹介を受けましたか？</div>
            <Selector
              selectedOption={formik.values.introductionConfirmation}
              setSelectedOption={(val) => formik.setFieldValue('introductionConfirmation', val)}
              options={IntroductionConfirmationStrs}
              error={formik.touched.introductionConfirmation ? formik.errors.introductionConfirmation : ''}
            />
          </div> */}
          <Button
            label="申込内容の確認へ"
            buttonFill={ButtonFill.black}
            buttonSize={ButtonSize.large}
            disable={!canApplySymbol}
            onClick={() => {
              showMessageModal(
                `お申込み金額は${formatPrice(
                  formik.values.amount!,
                )}円です。\n金額にお間違えのないようお手続きを進めてください。\n※お振込金額はお申込み金額に経過利息を加えた金額となります。\n\n適合性チェックがお済でないかたは、適合性チェックを行っていただいてから「申込内容の確認」にお進みいただきます。`,
                () => {
                  const id = crypto.randomUUID();
                  formik.setFieldValue('clientRequestId', id);
                  formik.handleSubmit();
                },
                '申込内容の確認へ',
              );
            }}
          />
          <MessageModal {...modalProps} shouldCloseOnOverlayClick={false} />
        </div>
      </div>
    </div>
  );
};
